import { Box, Button as MuiButton } from '@mui/material';
import { useMemo } from 'react';
import {
  buildButtonContentSx,
  buildButtonSx,
  BUTTON_LOADING_OVERLAY_SX,
  BUTTON_LOADING_SPINNER_SIZE,
} from './Button.styles';
import { CircularProgress } from './CircularProgress';
import { Icon } from './Icon';
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import type { ElementType, HTMLAttributeAnchorTarget, ReactElement, ReactNode } from 'react';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'contained'
  | 'outlined'
  | 'text';
type ButtonSize = 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large';
type ButtonVisualSize = 'sm' | 'md' | 'lg';

export type ButtonProps<C extends ElementType = 'button'> = Omit<
  MuiButtonProps<C>,
  'variant' | 'size' | 'startIcon' | 'endIcon' | 'loading'
> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidthOnMobile?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  target?: HTMLAttributeAnchorTarget;
  to?: string;
};

const resolveLoadingAriaLabel = (children: ReactNode, ariaLabel: string | undefined): string => {
  if (typeof ariaLabel === 'string' && ariaLabel.trim().length > 0) {
    return ariaLabel;
  }
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  return 'Loading';
};

export const Button = <C extends ElementType = 'button'>({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidthOnMobile = true,
  'aria-label': ariaLabel,
  color,
  disabled,
  children,
  startIcon,
  endIcon,
  sx,
  ...rest
}: ButtonProps<C>): ReactElement => {
  const mappedVariant = useMemo(() => {
    if (variant === 'secondary') return 'outlined';
    if (variant === 'ghost') return 'text';
    if (variant === 'destructive') return 'outlined';
    if (variant === 'primary') return 'contained';
    return variant;
  }, [variant]) as MuiButtonProps['variant'];
  const mappedColor: MuiButtonProps['color'] = variant === 'destructive' ? 'error' : color;

  const resolvedSize: ButtonVisualSize =
    size === 'small' ? 'sm' : size === 'large' ? 'lg' : size === 'medium' ? 'md' : size;
  const isSecondary = mappedVariant === 'outlined';
  const isDestructive = variant === 'destructive';
  const isErrorOutlined = mappedVariant === 'outlined' && mappedColor === 'error';
  const isDisabled = disabled === true || loading;

  const iconSize = resolvedSize;

  const mergedSx = buildButtonSx({
    fullWidthOnMobile,
    isDestructive,
    isErrorOutlined,
    isSecondary,
    loading,
    sx,
    visualSize: resolvedSize,
  });

  const loadingAriaLabel = resolveLoadingAriaLabel(children, ariaLabel);

  return (
    <MuiButton
      variant={mappedVariant}
      color={mappedColor}
      aria-busy={loading || undefined}
      aria-label={loading ? loadingAriaLabel : ariaLabel}
      disabled={isDisabled}
      startIcon={startIcon ? <Icon size={iconSize}>{startIcon}</Icon> : undefined}
      endIcon={endIcon ? <Icon size={iconSize}>{endIcon}</Icon> : undefined}
      sx={mergedSx}
      {...rest}
    >
      <Box component="span" sx={buildButtonContentSx({ loading })}>
        {children}
      </Box>
      {loading ? (
        <Box component="span" sx={BUTTON_LOADING_OVERLAY_SX}>
          <CircularProgress size={BUTTON_LOADING_SPINNER_SIZE} color="inherit" />
        </Box>
      ) : null}
    </MuiButton>
  );
};
